/**
 * SaveChangesDialog Component
 *
 * Preview dialog for pending changes in table explorer edit mode.
 * Shows the generated SQL (from Electron) and allows user to execute or cancel.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Code2, Save } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../../components/ui/dialog';
import { Button } from '../../../../components/ui/button';
import { QueryPreviewDialog } from './QueryPreviewDialog';
import type { PendingChange } from '../../core/types/workspaceTypes';
import type { TableExplorerApplyChangesResult } from '../../../../types/tableExplorer';
import { previewTableExplorerChanges } from '../services/tableExplorerApi';
import type { DatabaseEngineId } from '@/core/engines';

import { RandomDotmSpinner } from '@/components/ui/RandomDotmSpinner';
type SaveChangesDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  connectionId: string;
  engineId?: DatabaseEngineId;
  database: string;
  changes: PendingChange[];
  schema: string;
  tableName: string;
  supportsQueryPreview: boolean;
  onApply: (changes: PendingChange[]) => Promise<TableExplorerApplyChangesResult>;
  onOpenInQueryEditor?: (query: string) => void;
};

export function SaveChangesDialog({
  open,
  onOpenChange,
  connectionId,
  engineId,
  database,
  changes,
  schema,
  tableName,
  supportsQueryPreview,
  onApply,
  onOpenInQueryEditor,
}: SaveChangesDialogProps) {
  const fullTableName = schema ? `${schema}.${tableName}` : tableName;
  const [isExecuting, setIsExecuting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewSql, setPreviewSql] = useState('');

  const { insertCount, updateCount, deleteCount } = useMemo(() => {
    return {
      insertCount: changes.filter((c) => c.type === 'insert').length,
      updateCount: changes.filter((c) => c.type === 'update').length,
      deleteCount: changes.filter((c) => c.type === 'delete').length,
    };
  }, [changes]);

  useEffect(() => {
    if (open) return;
    setError(null);
    setIsExecuting(false);
    setIsPreviewOpen(false);
    setPreviewSql('');
  }, [open]);

  const handlePreviewEditQuery = useCallback(async () => {
    if (!supportsQueryPreview) return;

    // Note: schema can be empty for SQLite (which doesn't use schemas)
    if (!connectionId || !database || !tableName || changes.length === 0) {
      setPreviewSql('-- Missing context for SQL preview');
      setIsPreviewOpen(true);
      return;
    }

    setPreviewSql('-- Loading...');
    setIsPreviewOpen(true);

    try {
      const result = await previewTableExplorerChanges({
        connectionId,
        table: { database, schema, tableName },
        changes,
      });

      if (result.success) {
        setPreviewSql(result.sql || '');
        return;
      }

      setPreviewSql(`-- Failed to generate preview: ${result.error || 'Unknown error'}`);
    } catch (err) {
      setPreviewSql(`-- Failed to generate preview: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  }, [supportsQueryPreview, connectionId, database, tableName, changes, schema]);

  const handleExecute = useCallback(async (): Promise<TableExplorerApplyChangesResult> => {
    setIsExecuting(true);
    setError(null);

    try {
      const result = await onApply(changes);
      if (result.success) {
        // Defer the outer close so Radix' RemoveScroll for the inner
        // QueryPreviewDialog can fully unmount (and clear its body
        // pointer-events lock) before this outer dialog tears down. Without
        // the delay, both modal dialogs unmount in the same React commit
        // and Radix' two cleanups race — leaving `<body>` stuck with
        // `pointer-events: none`. See QuickEditDialog.handleExecute for the
        // full rationale; both dialogs share the same stacked-modal pattern.
        setTimeout(() => onOpenChange(false), 150);
        return result;
      }
      const errMsg = result.error || 'Failed to execute changes. Please check the SQL and try again.';
      setError(errMsg);
      return { success: false, error: errMsg };
    } catch (err) {
      const errMsg = err instanceof Error ? err.message : 'An error occurred';
      setError(errMsg);
      return { success: false, error: errMsg };
    } finally {
      setIsExecuting(false);
    }
  }, [onApply, changes, onOpenChange]);

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className="sm:max-w-2xl"
          data-testid="save-changes-dialog"
        >
          <DialogHeader>
            <DialogTitle>Save Changes</DialogTitle>
            <DialogDescription>
              The following changes will be applied to {fullTableName}:
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Summary badges */}
            <div className="flex gap-2 flex-wrap">
              {insertCount > 0 && (
                <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                  {insertCount} INSERT{insertCount > 1 ? 'S' : ''}
                </span>
              )}
              {updateCount > 0 && (
                <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400">
                  {updateCount} UPDATE{updateCount > 1 ? 'S' : ''}
                </span>
              )}
              {deleteCount > 0 && (
                <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400">
                  {deleteCount} DELETE{deleteCount > 1 ? 'S' : ''}
                </span>
              )}
            </div>

            {/* Warning */}
            <div className="flex items-start gap-2 text-sm text-muted-foreground">
              <AlertTriangle className="h-4 w-4 text-warning mt-0.5 shrink-0" />
              <span>This action cannot be undone. Please review the changes carefully.</span>
            </div>

            {/* Error message */}
            {error && (
              <div className="p-3 rounded-md bg-destructive/10 text-destructive text-sm">
                {error}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isExecuting}
            >
              Cancel
            </Button>

            {supportsQueryPreview && (
              <Button
                variant="outline"
                onClick={handlePreviewEditQuery}
                disabled={isExecuting || changes.length === 0}
                data-testid="preview-edit-query-button"
              >
                <Code2 className="h-4 w-4 mr-2" />
                Preview edit query
              </Button>
            )}

            <Button
              onClick={handleExecute}
              disabled={isExecuting || changes.length === 0}
              data-testid="execute-changes-button"
            >
              {isExecuting ? (
                <>
                  <RandomDotmSpinner size={16} className="mr-2" />
                  Executing...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Execute Changes
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {supportsQueryPreview && (
        <QueryPreviewDialog
          open={isPreviewOpen}
          onOpenChange={setIsPreviewOpen}
          query={previewSql}
          engineId={engineId}
          onOpenInQueryEditor={
            onOpenInQueryEditor
              ? (query: string) => {
                  onOpenInQueryEditor(query);
                  onOpenChange(false);
                }
              : undefined
          }
          onExecute={handleExecute}
          executeLabel="Execute Changes"
          executingLabel="Executing..."
          executeDisabled={changes.length === 0}
          title="Edit Query Preview"
          description={`Generated SQL script for ${fullTableName}`}
          testIdPrefix="edit-query-preview"
        />
      )}
    </>
  );
}
